interface EventDateOptions {
  timeZone?: string | null;
  naiveIsLocal?: boolean;
  includeSeconds?: boolean;
}

function eventDate(value: string, { timeZone, naiveIsLocal }: EventDateOptions) {
  const explicitOffset = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(value);
  return {
    date: new Date(explicitOffset ? value : `${value}Z`),
    timeZone: !explicitOffset && naiveIsLocal ? 'UTC' : timeZone || undefined,
  };
}

/** UTC API timestamps are displayed in the camera location's timezone.
 * During rolling upgrades, old NVR timestamps without an offset are UTC;
 * old cloud timestamps without an offset are already location wall time.
 */
export function formatEventDate(value?: string, { timeZone, naiveIsLocal = false, includeSeconds = false }: EventDateOptions = {}): string {
  if (!value) return 'N/A';
  const { date, timeZone: zone } = eventDate(value, { timeZone, naiveIsLocal });
  if (!Number.isFinite(date.getTime())) return 'Invalid date';
  const options: Intl.DateTimeFormatOptions = {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', ...(includeSeconds ? { second: '2-digit' } : {}), hour12: true,
  };
  try {
    return new Intl.DateTimeFormat('en-US', { ...options, timeZone: zone }).format(date);
  } catch {
    // A stale/invalid location setting must not hide the event's timestamp.
    return new Intl.DateTimeFormat('en-US', options).format(date);
  }
}

/** EZOPEN expects the NVR's wall clock, regardless of the browser timezone. */
export function getEventPlaybackTimes(value: string, options: EventDateOptions = {}, bufferSeconds = 60) {
  const { date, timeZone } = eventDate(value, options);
  if (!Number.isFinite(date.getTime())) throw new Error('Invalid event time');
  const formatForPlayer = (instant: Date) => {
    const fields = { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' } as const;
    let formatter: Intl.DateTimeFormat;
    try { formatter = new Intl.DateTimeFormat('en-US', { ...fields, timeZone }); }
    catch { formatter = new Intl.DateTimeFormat('en-US', fields); }
    const parts = formatter.formatToParts(instant);
    const part = (type: string) => parts.find(value => value.type === type)?.value;
    return `${part('year')}-${part('month')}-${part('day')}T${part('hour')}:${part('minute')}:${part('second')}`;
  };
  return {
    start: formatForPlayer(new Date(date.getTime() - bufferSeconds * 1000)),
    end: formatForPlayer(new Date(date.getTime() + bufferSeconds * 1000)),
  };
}
